// ============================================================================
// Subscription Service
// Resolves the campground's Analysis feature entitlement from hcp-saas
// (which owns FeatureTemplate → FeatureInstance → Subscription).
// Calls hcp-saas directly on a second base URL, the same pattern as
// AIAgentConfigCard's REACT_APP_AIAGENT_API_URL.
// ============================================================================

import { authService } from './auth/authService';

const SAAS_API_URL = process.env.REACT_APP_SAAS_API_URL || 'http://localhost:8010';

export type EntitlementStatus =
  | 'active'
  | 'trial'
  | 'expired_grace'
  | 'expired'
  | 'cancelled'
  | 'none';

export type AnalysisCategory =
  | 'sales'
  | 'occupancy'
  | 'product'
  | 'payments'
  | 'cancellations'
  | 'anomalies'
  | 'members'
  | 'pacing';

export interface AnalysisEntitlement {
  status: EntitlementStatus;
  tier: 'starter' | 'professional' | 'enterprise' | null;
  enabled_categories: AnalysisCategory[];
  trial_days_remaining?: number;
  grace_days_remaining?: number;
  expires_at: string | null;
}

export const NO_ENTITLEMENT: AnalysisEntitlement = {
  status: 'none',
  tier: null,
  enabled_categories: [],
  expires_at: null,
};

async function fetchEntitlementFor(corporateCode: string, featureType: string): Promise<AnalysisEntitlement> {
  const response = await fetch(
    `${SAAS_API_URL}/api/public/entitlements/${encodeURIComponent(corporateCode)}?feature_type=${featureType}`
  );
  if (!response.ok) {
    throw new Error(`Entitlement check failed (${response.status})`);
  }
  return response.json();
}

export async function fetchAnalysisEntitlement(): Promise<AnalysisEntitlement> {
  const corporateCode = authService.getCorporateCode();
  if (!corporateCode) return NO_ENTITLEMENT;

  const full = await fetchEntitlementFor(corporateCode, 'ANALYTICS_DASHBOARD');
  if (full.status !== 'none') return full;

  // Packaging fallback: a standalone SALES_REPORT subscription unlocks
  // just the Sales category.
  const salesOnly = await fetchEntitlementFor(corporateCode, 'SALES_REPORT');
  if (salesOnly.status === 'none') return full;
  return {
    ...salesOnly,
    enabled_categories:
      salesOnly.enabled_categories.length > 0
        ? salesOnly.enabled_categories.filter(c => c === 'sales')
        : hasAnalysisAccess(salesOnly)
          ? ['sales']
          : [],
  };
}

/** True when the entitlement grants access to the Analysis views at all. */
export function hasAnalysisAccess(entitlement: AnalysisEntitlement): boolean {
  return (
    entitlement.status === 'active' ||
    entitlement.status === 'trial' ||
    entitlement.status === 'expired_grace'
  );
}
